import type { Components } from 'react-markdown';
import type { PluggableList } from 'unified';

import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { parsePreviewHref } from '@elara/client-core/lib/route-utils';
import { cn } from '@elara/client-core/lib/utils';

import { PreviewLink } from './PreviewLink.js';

export interface SimpleMarkdownProps {
  text: string;
  className?: string;
}

// Create remark plugins array once at module level to avoid recreating on every render
// Disable strikethrough because agents frequently use ~ for approximate prices (e.g. ~$200),
// which GFM interprets as strikethrough markup.
const REMARK_PLUGINS = [[remarkGfm, { singleTilde: false }], remarkBreaks] as PluggableList;

function isExternalHref(href: string | undefined): boolean {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return url.host !== window.location.host;
  } catch {
    return false;
  }
}

// Custom components for ReactMarkdown - defined at module level to avoid recreating on every render
const MARKDOWN_COMPONENTS: Components = {
  a: ({ href, children }) => {
    const previewInfo = parsePreviewHref(href ?? '');
    if (previewInfo) {
      return <PreviewLink {...previewInfo}>{children}</PreviewLink>;
    }
    if (isExternalHref(href)) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    return <a href={href}>{children}</a>;
  },
};

/**
 * Simple markdown renderer without widget support.
 * Used by RecommendationWidget to avoid circular dependencies.
 * Memoized to prevent expensive ReactMarkdown re-renders when typing in the input.
 */
function SimpleMarkdownComponent({ text, className }: SimpleMarkdownProps) {
  // Memoize the rendered markdown to prevent re-parsing when text hasn't changed
  const renderedMarkdown = useMemo(
    () => (
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
        {text}
      </ReactMarkdown>
    ),
    [text],
  );

  return (
    <div
      className={cn(
        `inline-block prose prose-sm dark:prose-invert max-w-full break-words space-y-4 markdown-content`,
        className,
      )}
    >
      {renderedMarkdown}
    </div>
  );
}

export const SimpleMarkdown = memo(SimpleMarkdownComponent);
